import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc, collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../firebase-config';

const CourseDetail = () => {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const courseRef = doc(db, 'courses', id);
        const courseSnap = await getDoc(courseRef);
        
        if (courseSnap.exists()) {
          const data = { id: courseSnap.id, ...courseSnap.data() };
          setCourse(data);

          // Load related courses
          const q = query(
            collection(db, 'courses'),
            where('category', '==', data.category),
            orderBy('courseName', 'asc'),
            limit(3)
          );
          const relatedSnap = await getDocs(q);
          const relatedData = [];
          relatedSnap.forEach(doc => {
            if (doc.id !== id) relatedData.push({ id: doc.id, ...doc.data() });
          });
          setRelated(relatedData);
        }
      } catch (error) {
        console.error('Error loading course:', error);
      }
      setLoading(false);
    };
    loadData();
  }, [id]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ width: '50px', height: '50px', borderRadius: '50%', border: '4px solid rgba(14,165,233,0.1)', borderTopColor: 'var(--clr-blue)', animation: 'spin 0.8s linear infinite' }}></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '80px 0' }}>
        <i className="fas fa-exclamation-circle" style={{ fontSize: '3rem', color: 'var(--clr-rose)' }}></i>
        <h2 style={{ marginTop: '20px' }}>Course not found</h2>
        <Link to="/courses" style={{ display: 'inline-block', marginTop: '20px', padding: '12px 28px', background: 'linear-gradient(135deg, var(--clr-blue), #0284c7)', borderRadius: '40px', color: 'white' }}>Back to Courses</Link>
      </div>
    );
  }

  const fee = course.courseFee ? parseInt(course.courseFee).toLocaleString('en-IN') : 'Contact for Fee';
  const eligibilityList = course.eligibility ? course.eligibility.split('\n') : ['10+2 with minimum 50% marks', 'Valid entrance exam score'];
  const documentsList = course.documents ? course.documents.split('\n') : ['10th & 12th Mark Sheets', 'Transfer Certificate', 'Migration Certificate'];

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link to="/courses" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '30px', padding: '12px 24px', background: 'rgba(255,255,255,0.4)', borderRadius: '50px', transition: 'all 0.3s ease' }}>
          <i className="fas fa-arrow-left"></i> Back to Courses
        </Link>

        <div style={{
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(20px) saturate(140%)',
          borderRadius: '24px',
          overflow: 'hidden',
          border: '1px solid var(--clr-blue-border)'
        }}>
          <img
            src={course.featuredImage || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200'}
            alt={course.courseName}
            style={{ width: '100%', height: '400px', objectFit: 'cover' }}
          />

          <div style={{ padding: '40px' }}>
            <div style={{
              display: 'inline-block',
              padding: '6px 16px',
              background: course.active ? 'var(--clr-emerald)' : '#94a3b8',
              color: 'white',
              borderRadius: '30px',
              fontSize: '0.8rem',
              fontWeight: 600,
              marginBottom: '20px'
            }}>
              {course.active ? 'Active' : 'Inactive'}
            </div>

            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2.5rem', marginBottom: '20px' }}>{course.courseName}</h1>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '20px',
              margin: '30px 0'
            }}>
              {[
                { icon: 'fa-clock', label: 'Duration', value: course.duration || 'N/A' },
                { icon: 'fa-rupee-sign', label: 'Total Fee', value: `₹${fee}` },
                { icon: 'fa-users', label: 'Intake', value: course.intake || '60 Seats' }
              ].map((item, index) => (
                <div key={index} style={{
                  background: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(20px) saturate(140%)',
                  borderRadius: '16px',
                  padding: '20px',
                  textAlign: 'center',
                  border: '1px solid var(--clr-blue-border)'
                }}>
                  <i className={`fas ${item.icon}`} style={{ fontSize: '2rem', color: 'var(--clr-blue)', marginBottom: '10px' }}></i>
                  <h4 style={{ fontSize: '1rem', marginBottom: '5px' }}>{item.label}</h4>
                  <p style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--clr-emerald)' }}>{item.value}</p>
                </div>
              ))}
            </div>

            {/* Eligibility */}
            <div style={{
              margin: '30px 0',
              padding: '20px',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '16px'
            }}>
              <h3 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-graduation-cap" style={{ color: 'var(--clr-blue)' }}></i> Eligibility Criteria
              </h3>
              <ul style={{ listStyle: 'none', paddingLeft: '20px' }}>
                {eligibilityList.map((item, i) => (
                  <li key={i} style={{ marginBottom: '8px', position: 'relative', paddingLeft: '20px' }}>
                    <span style={{ position: 'absolute', left: 0, color: 'var(--clr-emerald)' }}>✓</span> {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Documents */}
            <div style={{
              margin: '30px 0',
              padding: '20px',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '16px'
            }}>
              <h3 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-file-alt" style={{ color: 'var(--clr-blue)' }}></i> Documents Required
              </h3>
              <ul style={{ listStyle: 'none', paddingLeft: '20px' }}>
                {documentsList.map((item, i) => (
                  <li key={i} style={{ marginBottom: '8px', position: 'relative', paddingLeft: '20px' }}>
                    <span style={{ position: 'absolute', left: 0, color: 'var(--clr-emerald)' }}>✓</span> {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Description */}
            <div style={{ margin: '30px 0' }}>
              <h3 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-info-circle" style={{ color: 'var(--clr-blue)' }}></i> About the Course
              </h3>
              <p style={{ lineHeight: '1.8', color: 'var(--text-secondary)' }}>{course.description || 'No detailed description available for this course.'}</p>
            </div>

            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '20px' }}>
              <button
                onClick={() => setShowDialog(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 32px',
                  background: 'linear-gradient(135deg, var(--clr-amber), #d97706)',
                  border: 'none',
                  borderRadius: '40px',
                  color: 'white',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <i className="fas fa-paper-plane"></i> Apply Now
              </button>
              <Link to="/courses" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 32px',
                background: 'linear-gradient(135deg, var(--clr-blue), #0284c7)',
                borderRadius: '40px',
                color: 'white',
                fontWeight: 700,
                textDecoration: 'none',
                transition: 'all 0.3s ease'
              }}>
                <i className="fas fa-arrow-left"></i> Back to Courses
              </Link>
            </div>
          </div>
        </div>

        {/* Related Courses */}
        {related.length > 0 && (
          <div style={{ marginTop: '60px' }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', marginBottom: '25px' }}>
              <i className="fas fa-graduation-cap" style={{ color: 'var(--clr-blue)' }}></i> Other Courses
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '24px'
            }}>
              {related.map(item => (
                <Link key={item.id} to={`/courses/${item.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(20px) saturate(140%)',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    border: '1px solid var(--clr-blue-border)',
                    transition: 'all 0.3s ease'
                  }}>
                    <img
                      src={item.featuredImage || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400'}
                      alt={item.courseName}
                      style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                    />
                    <div style={{ padding: '20px' }}>
                      <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '8px' }}>{item.courseName}</h3>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Duration: {item.duration || 'N/A'}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Dialog */}
      {showDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.8)',
            zIndex: 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setShowDialog(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fas fa-info-circle" style={{ fontSize: '4rem', color: 'var(--clr-amber)', marginBottom: '15px' }}></i>
            <h3 style={{ marginBottom: '10px' }}>Application</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>Application process will start soon. Please check back later.</p>
            <button
              onClick={() => setShowDialog(false)}
              style={{
                padding: '10px 24px',
                background: 'linear-gradient(135deg, var(--clr-blue), #0284c7)',
                border: 'none',
                borderRadius: '40px',
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetail;